import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { Subject } from 'rxjs';
import { pairwise, startWith, takeUntil, tap } from 'rxjs/operators';

import {
  BackendObjectRef,
  FormField,
  FormFieldTypeID,
  FormFieldTypeUserField,
} from '@fry/booking/workflow-editor/lib';

export interface FormFieldFilterValue {
  operator?: string;
  target?: string;
  value?: string|number|(string|number)[];
}

export type FormFieldFilterTargetType = 'primitive'|'discrete';

export interface FormFieldFilterOperator {
  id: string,
  name: string,
  targetType: FormFieldFilterTargetType;
}

@Component({
    selector: 'eas-workflow-form-field-filter-control',
    templateUrl: './form-field-filter.control.html',
    styleUrls: ['./form-field-filter.control.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowFormFieldFilterControl),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => WorkflowFormFieldFilterControl),
            multi: true
        }
    ],
    standalone: false
})
export class WorkflowFormFieldFilterControl implements OnInit, OnDestroy, ControlValueAccessor, Validator {

  /**
   * List of available form fields user can select using this control
   */
  @Input() availableFields: FormField[] = [];

  public readonly availableOperators: FormFieldFilterOperator[] = [
    { id: 'eq',      name: $localize `equals` , targetType: 'primitive'},
    { id: 'has_any', name: $localize `has any`, targetType: 'discrete'},
    { id: 'has_all', name: $localize `has all`, targetType: 'discrete'},
  ];

  private destroyed$ = new Subject<void>();

  public readonly form: FormGroup;

  public targetIsBooleanType: boolean = false;
  public targetHasDiscreteValues: boolean = false;
  public targetDiscreteValues: BackendObjectRef[] = [];
  public targetScopedOperators: FormFieldFilterOperator[] = [];

  public targetFieldLabel = $localize `Field name`;
  public operatorFieldLabel = $localize `Operator`;
  public valueFieldLabel = $localize `Field value`;

  private get targetControl(): FormControl {
    return this.form.get('target') as FormControl;
  }

  public constructor(formBuilder: FormBuilder) {
    this.form = formBuilder.group({
      target: ['', Validators.required],
      operator: ['', Validators.required],
      value: ['', Validators.required],
    });
  }

  private getTargetDiscreteValues(target?: FormField): BackendObjectRef[] {
    if (!target) { return []; }

    const values = target.type instanceof FormFieldTypeUserField
                   ? target.type.availableValues
                   : target.availableValues;
    return values ?? [];
  }

  private getTargetScopedOperators(target: FormField): FormFieldFilterOperator[] {
    return this.availableOperators.filter(op => {
      return this.isTargetDiscreteType(target)
             ? op.targetType === 'discrete'
             : op.targetType === 'primitive';
    });
  }

  private isTargetBooleanType(target?: FormField): boolean {
    return (target?.type.id === FormFieldTypeID.boolean);
  }

  private isTargetDiscreteType(target?: FormField): boolean {
    return target?.type.isDiscrete ?? false;
  }

  /**
   * Updates this control's child controls based on the selected target field.
   *
   * @param targetValue ID of the target field
   */
  private updateControlForTarget(targetValue?: string): void {
    if (!targetValue) { return; }

    const field = this.availableFields.find(t => t.id === targetValue);
    if (!field) { return; }

    this.targetIsBooleanType = this.isTargetBooleanType(field);
    this.targetHasDiscreteValues = this.isTargetDiscreteType(field);
    this.targetDiscreteValues = this.getTargetDiscreteValues(field);
    this.targetScopedOperators = this.getTargetScopedOperators(field);
  }

  //
  // Lifecycle
  //

  public ngOnInit(): void {
    this.targetControl
        .valueChanges
        .pipe(
          startWith(this.targetControl.value),
          pairwise(),
          takeUntil(this.destroyed$)
        )
        .subscribe(([oldTarget, newTarget]) => {
          this.updateControlForTarget(newTarget);
          if (oldTarget !== newTarget && (oldTarget ?? '') !== '') {
            this.form.patchValue({value: undefined});
          }
        });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  //
  // Validator interface
  //

  validate(_control: FormControl): ValidationErrors | null {
    const errors: ValidationErrors = {
      invalidFormFieldFilter: {
        valid: false,
        message: $localize `Form field filter settings is not valid!`
      }
    };
    // tslint:disable-next-line: no-null-keyword
    return this.form.valid ? null : errors;
  }

  //
  // ControlValueAccessor interface
  //

  public onTouched: () => void = () => {};

  writeValue(value: FormFieldFilterValue): void {
    if (!value) { return; }

    const target = this.availableFields.find(f => f.id === value.target);
    if (this.isTargetDiscreteType(target) && value.value !== undefined) {
        value.value = value.value instanceof Array ? value.value : [value.value];
    }

    const defaults: FormFieldFilterValue = {};
    this.form.setValue({...defaults, ...value}, { emitEvent: false });
    this.updateControlForTarget(value.target);
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
             .pipe(
               takeUntil(this.destroyed$),
               tap(() => this.onTouched())
             )
             .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (this.form.enabled && isDisabled) {
      this.form.disable();
    }
    if (this.form.disabled && !isDisabled) {
      this.form.enable();
    }
  }
}
